import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap, catchError, first, filter, timeout } from 'rxjs/operators';
import firebase from 'firebase/app';
import 'firebase/auth';
import { AppHttpClientService } from '../shared/app-http-client.service';
import { CurrentUserResponse } from '../admin/users/system-users.model';
import { PermissionsService } from './permissions.service';
import { CurrentUser } from './current-user.model';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private readonly currentUserSubject = new BehaviorSubject<CurrentUser | null>(null);
  private readonly isLoadingSubject = new BehaviorSubject<boolean>(false);

  public readonly currentUser$ = this.currentUserSubject.asObservable();
  public readonly isLoading$ = this.isLoadingSubject.asObservable();

  constructor(
    public auth: AngularFireAuth,
    private readonly httpClient: AppHttpClientService,
    private readonly permissionsService: PermissionsService
  ) {
    this.initializeAuthState();
  }

  /**
   * Initialize authentication state listener
   */
  private initializeAuthState(): void {
    this.auth.authState.pipe(
      switchMap(firebaseUser => {
        if (firebaseUser) {
          return this.getCurrentSystemUser();
        } else {
          this.currentUserSubject.next(null);
          return of(null);
        }
      }),
      catchError(error => {
        console.error('Error initializing auth state:', error);
        this.currentUserSubject.next(null);
        return of(null);
      })
    ).subscribe();
  }

  login(uname: string, pwd: string): Promise<CurrentUser> {
    return this.auth.signInWithEmailAndPassword(uname, pwd).then(() => {
      return this.waitForUserLoad();
    });
  }

  changePwd(actualPwd: string, newPwd: string) {
    return new Promise((resolve, reject) => {
      this.auth.currentUser
        .then((user) => {
          const credential = firebase.auth.EmailAuthProvider.credential(user.email, actualPwd);
          user
            .reauthenticateWithCredential(credential)
            .then(() => {
              user
                .updatePassword(newPwd)
                .then(() => resolve(true))
                .catch((reason) => reject(reason));
            })
            .catch((reason) => reject(reason));
        })
        .catch((reason) => reject(reason));
    });
  }

  sendRememberPwd(email: string) {
    return new Promise((resolve, reject) => {
      this.auth
        .sendPasswordResetEmail(email)
        .then(() => resolve(true))
        .catch((reason) => reject(reason));
    });
  }

  logout() {
    this.currentUserSubject.next(null);
    return this.auth.signOut();
  }

  get token() {
    return this.auth.idToken.pipe(first());
  }

  /**
   * Get current system user with permissions
   */
  getCurrentSystemUser(): Observable<CurrentUser | null> {
    this.isLoadingSubject.next(true);

    return this.httpClient.get<CurrentUserResponse>('system-users/me').pipe(
      map(response => {
        if (!response?.profile) {
          this.isLoadingSubject.next(false);
          return null;
        }

        const currentUser: CurrentUser = {
          uid: response.profile.uid,
          email: response.profile.email,
          name: response.profile.name,
          roleIds: response.profile.roleIds || [],
          permissions: response.permissions || []
        };
        
        this.currentUserSubject.next(currentUser);
        this.isLoadingSubject.next(false);
        return currentUser;
      }),
      catchError(error => {
        console.error('Error getting current system user:', error);
        this.isLoadingSubject.next(false);
        return of(null);
      })
    );
  }

  /**
   * Check if user has a specific permission
   */
  hasPermission(permissionId: string): boolean {
    const currentUser = this.currentUserSubject.value;
    if (!currentUser) {
      return false;
    }
    return this.permissionsService.hasPermission(currentUser.permissions, permissionId);
  }

  /**
   * Check if user has any of the specified permissions (OR logic)
   */
  hasAnyPermission(permissionIds: string[]): boolean {
    const currentUser = this.currentUserSubject.value;
    if (!currentUser) {
      return false;
    }
    return this.permissionsService.hasAnyPermission(currentUser.permissions, permissionIds);
  }

  /**
   * Check if user has all specified permissions (AND logic)
   */
  hasAllPermissions(permissionIds: string[]): boolean {
    const currentUser = this.currentUserSubject.value;
    if (!currentUser) {
      return false;
    }
    return this.permissionsService.hasAllPermissions(currentUser.permissions, permissionIds);
  }

  /**
   * Refresh user permissions (reload)
   */
  refreshPermissions(): Observable<CurrentUser | null> {
    return this.getCurrentSystemUser();
  }

  /**
   * Get current user name
   */
  getCurrentUserName(): Observable<string> {
    return this.currentUser$.pipe(
      map(user => user?.name || 'Usuario')
    );
  }

  /**
   * Get current user value (synchronous)
   */
  getCurrentUserValue(): CurrentUser | null {
    return this.currentUserSubject.value;
  }

  /**
   * Wait for user to be loaded after login
   * Returns a promise that resolves when the user is available
   */
  waitForUserLoad(): Promise<CurrentUser> {
    return new Promise((resolve, reject) => {
      this.auth.authState.pipe(
        filter(firebaseUser => firebaseUser !== null),
        first(),
        switchMap(() => this.currentUser$.pipe(
          filter(user => user !== null),
          first()
        )),
        timeout(10000)
      ).subscribe(
        (user) => {
          resolve(user);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }
}
